import {
  useGetCcmInteractionTime,
  useGetRpmInteractionTime,
} from 'components/organisms/interactiveTime/hooks';
import { useTranslation } from 'react-i18next';
import { DateFormats, ModuleType } from 'infrastructure/enums';
import { DateFormatter } from 'infrastructure/functions';
import { Flex, Typography, Skeleton } from 'antd';
import { useMemo, type FC } from 'react';

import ModuleTime from './module-time';
import s from './styles.module.scss';

const { Text } = Typography;

const ACTION_TIME_QUOTA_55 = 55 * 60;

const checkActionTimeQuota = (totalTime?: number) => {
  if (!totalTime) return false;

  return totalTime >= ACTION_TIME_QUOTA_55;
};

interface InteractiveTimeProps {
  patientGuid: string;
}

const InteractiveTime: FC<InteractiveTimeProps> = ({ patientGuid }) => {
  const { ccmInteractionTime, isLoading: isCcmLoading } =
    useGetCcmInteractionTime({
      patientGuid,
      enabled: true,
    });
  const { rpmInteractionTime, isLoading: isRpmLoading } =
    useGetRpmInteractionTime({
      patientGuid,
      enabled: true,
    });
  const { t } = useTranslation();

  const today = new Date();

  const rpmQuotaAccent = checkActionTimeQuota(rpmInteractionTime?.totalTime);

  const isLoading = useMemo(
    () => isCcmLoading || isRpmLoading,
    [isCcmLoading, isRpmLoading],
  );

  return (
    <div className={s.time}>
      <Flex align="center" justify="space-between" className={s['time-header']}>
        <Text className={s['time-title']}>{t('labels.interactiveTime')}</Text>
        {isLoading ? (
          <Skeleton.Node active className={s['skeleton-month']} />
        ) : (
          <Text className={s['time-month']}>
            {DateFormatter({ date: today, format: DateFormats.MMMM })}
          </Text>
        )}
      </Flex>
      <Flex vertical data-time className={s['time-content']}>
        <ModuleTime
          clinicTime={rpmInteractionTime?.clinicTime}
          totalTime={rpmInteractionTime?.totalTime}
          module={ModuleType.RPM}
          accent={rpmQuotaAccent}
          isLoading={isLoading}
        />
        <div className={s.divider} />
        <ModuleTime
          clinicTime={ccmInteractionTime?.clinicTime}
          totalTime={ccmInteractionTime?.totalTime}
          module={ModuleType.CCM}
          isLoading={isLoading}
        />
      </Flex>
    </div>
  );
};
export default InteractiveTime;
